import { API } from 'src/utils/interceptor'

export async function fetchReconciliationToleranceSettings(venueId) {
  if (!venueId) {
    throw new Error('Missing venue id')
  }

  const response = await API.get(
    'api2',
    `venue/${venueId}/reconciliation-settings`,
  )

  if (response?.success === false) {
    throw new Error(
      response?.message || 'Could not load match tolerance settings',
    )
  }

  return response?.data || response
}

export async function updateVenueMatchTolerance(
  venueId,
  invoiceMatchTolerance,
) {
  const response = await API.patch(
    'api2',
    `venue/${venueId}/reconciliation-settings/invoice-match-tolerance`,
    { body: { invoiceMatchTolerance } },
  )

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not update venue tolerance')
  }

  return response?.data || response
}

export async function updateSupplierMatchTolerance(
  venueId,
  venueSupplierId,
  invoiceMatchTolerance,
) {
  const response = await API.patch(
    'api2',
    `venue/${venueId}/reconciliation-settings/suppliers/${venueSupplierId}/invoice-match-tolerance`,
    { body: { invoiceMatchTolerance } },
  )

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not update supplier tolerance')
  }

  return response?.data || response
}

/**
 * Apply a save plan from buildToleranceSavePlan, then rematch open invoices
 * so within/outside outcomes refresh, then return latest settings.
 */
export async function saveToleranceSettings(venueId, plan) {
  if (plan.venuePennies !== undefined) {
    await updateVenueMatchTolerance(venueId, plan.venuePennies)
  }

  for (const update of plan.supplierUpdates || []) {
    await updateSupplierMatchTolerance(
      venueId,
      update.venueSupplierId,
      update.invoiceMatchTolerance,
    )
  }

  const rematch = await rematchOpenInvoicesAfterToleranceChange(venueId, {
    venueToleranceChanged: plan.venuePennies !== undefined,
    venueSupplierIds: (plan.supplierUpdates || []).map(
      update => update.venueSupplierId,
    ),
  })

  const settings = await fetchReconciliationToleranceSettings(venueId)
  return { settings, rematch }
}

/**
 * Chunk 14 — rematch open invoices after Match tolerance save.
 */
export async function rematchOpenInvoicesAfterToleranceChange(
  venueId,
  { venueToleranceChanged = false, venueSupplierIds = [] } = {},
) {
  if (!venueId) {
    return {
      rematchedInvoiceIds: [],
      checked: 0,
      skipped: true,
      skipReason: 'missing_venue',
    }
  }

  try {
    const response = await API.post(
      'api2',
      `invoices/rematch-after-tolerance/${venueId}`,
      {
        body: {
          venueToleranceChanged: Boolean(venueToleranceChanged),
          venueSupplierIds: venueSupplierIds || [],
        },
      },
    )
    return response?.data || response || {}
  } catch (error) {
    // Settings already saved — do not fail the whole save on rematch errors.
    console.error('Tolerance rematch failed', error)
    return {
      rematchedInvoiceIds: [],
      checked: 0,
      skipped: true,
      skipReason: 'rematch_failed',
      error:
        error?.response?.data?.message || error?.message || 'Rematch failed',
    }
  }
}
